import React, { useCallback, useEffect, useState } from 'react';
import { SpAccount } from '../SpAccountContext';
import { getSchedulerConfig, updateSchedulerConfig } from '../api';

interface Props { account: SpAccount; }

interface TaskConfig {
  task_key: string;
  label: string;
  schedule: string;
  description: string;
  is_enabled: boolean;
  updated_at: string | null;
}

const TASK_ICONS: Record<string, string> = {
  orders_by_update: '🔄',
  orders_by_date:   '🛒',
  inventory:        '📦',
  fbm_inventory:    '🚚',
  business_reports: '📊',
};

const SpScheduler: React.FC<Props> = ({ account }) => {
  const [tasks, setTasks] = useState<TaskConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSchedulerConfig(account.seller_id);
      setTasks(res.data);
    } catch {
      setError('Failed to load scheduler configuration.');
    } finally {
      setLoading(false);
    }
  }, [account.seller_id]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (task: TaskConfig) => {
    setToggling(t => ({ ...t, [task.task_key]: true }));
    try {
      await updateSchedulerConfig(account.seller_id, task.task_key, !task.is_enabled);
      setTasks(prev => prev.map(t => t.task_key === task.task_key ? { ...t, is_enabled: !t.is_enabled, updated_at: new Date().toISOString() } : t));
    } catch {
      setError(`Failed to update ${task.label}.`);
    } finally {
      setToggling(t => ({ ...t, [task.task_key]: false }));
    }
  };

  const enabledCount = tasks.filter(t => t.is_enabled).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Automatic Data Pulls</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            {enabledCount} of {tasks.length} schedules active · Runs automatically for all marketplaces
          </div>
        </div>
        <div style={{
          padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          background: enabledCount > 0 ? 'var(--success)22' : 'var(--text-muted)22',
          color: enabledCount > 0 ? 'var(--success)' : 'var(--text-muted)',
          border: `1px solid ${enabledCount > 0 ? 'var(--success)' : 'var(--border)'}`,
        }}>
          {enabledCount > 0 ? 'Scheduler active' : 'All paused'}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--danger)11', color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner" /> Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tasks.map(task => {
            const isToggling = toggling[task.task_key];
            return (
              <div
                key={task.task_key}
                className="card"
                style={{
                  padding: '16px 20px',
                  opacity: task.is_enabled ? 1 : 0.7,
                  transition: 'opacity 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, justifyContent: 'space-between' }}>
                  {/* Left: icon + info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 26, flexShrink: 0 }}>{TASK_ICONS[task.task_key] ?? '⚙️'}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {task.label}
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 500,
                          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                          color: 'var(--text-muted)', flexShrink: 0,
                        }}>
                          {task.schedule}
                        </span>
                        {!task.is_enabled && (
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                            background: 'var(--warning)22', color: 'var(--warning)',
                            border: '1px solid var(--warning)',
                          }}>
                            Paused
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                        {task.description}
                      </div>
                      {task.updated_at && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          Last changed: {new Date(task.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: toggle button */}
                  <button
                    disabled={isToggling}
                    onClick={() => toggle(task)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 18px',
                      borderRadius: 6,
                      border: `1px solid ${task.is_enabled ? 'var(--warning)' : 'var(--success)'}`,
                      background: task.is_enabled ? 'var(--warning)18' : 'var(--success)18',
                      color: task.is_enabled ? 'var(--warning)' : 'var(--success)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: isToggling ? 'not-allowed' : 'pointer',
                      opacity: isToggling ? 0.6 : 1,
                      transition: 'all 0.15s',
                      minWidth: 90,
                    }}
                  >
                    {isToggling ? '…' : task.is_enabled ? '⏸ Pause' : '▶ Resume'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info note */}
      <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
        Schedules run in UTC for all active marketplaces on this account. Pausing a schedule only prevents future runs — any currently running pull will complete normally. Check <strong>Sync History</strong> to monitor recent pulls.
      </div>
    </div>
  );
};

export default SpScheduler;
